import classNames from 'classnames';
import { CSSProperties, useEffect, useImperativeHandle, useState } from 'react';
import {
  Button,
  ColorPicker,
  Loading,
  Space,
  Tooltip,
} from 'tdesign-react';

import { RichMediaValue } from '@/interface/media';
import { getColorsFromAi } from '@/services/color';

import styles from './index.module.less';

interface IProps {
  onChange?: (sampleValue: RichMediaValue['sample']) => void;
  value: RichMediaValue['sample'];
  url: string;
  isShowColorPicker?: boolean;
  getColorStatus: (loading: boolean) => void;
  miniPicker?: boolean;
  disabled?: boolean;
  colorRef?: React.MutableRefObject<
  | {
    getColor: (url: string) => Promise<{} | undefined>;
  }
  | undefined
  >;
  disabledBtn?: boolean;
}

const defaultColor = '';

const ERROR_TIPS = {
  ERROR: '获取颜色失败，请点击重试或自己输入主色和补色', // 组件提示
  ERROR_MINI: '获取颜色失败', // miniPicker错误提示
  NOT_IMAGE: '请上传图片后进行取色',
};

export const ColorPickerRichMedia: React.FC<IProps> = (props) => {
  const {
    value,
    url,
    miniPicker,
    disabled,
    colorRef,
    disabledBtn,
    isShowColorPicker = false,
    getColorStatus,
    onChange,
  } = props;

  useImperativeHandle(colorRef, () => ({
    getColor: async (url: string) => await getColors(url),
  }));

  const [loading, setLoading] = useState(false);

  const [errorTip, setErrorTip] = useState('');

  const { primary, complementary } = value || {};

  const [sample, setSample] = useState({
    primary: primary || defaultColor,
    complementary: complementary || defaultColor,
  });

  const handleOnChangeColor = (key: string, value: string) => {
    const data = {
      ...sample,
      [key]: value,
    };
    propsOnChange(data);
  };

  useEffect(() => {
    setSample({
      primary: primary || defaultColor,
      complementary: complementary || defaultColor,
    });
  }, [value]);

  const getColors = async (url: string) => {
    if (disabled) {
      return;
    }
    if (!url) {
      setErrorTip(ERROR_TIPS.NOT_IMAGE);
      return;
    }
    setErrorTip('');
    changeLoading(true);
    try {
      const { data } = await getColorsFromAi(url);
      changeLoading(false);
      if (data?.primary || data?.complementary) {
        propsOnChange(data);
        return data;
      }
      const error = miniPicker ? ERROR_TIPS.ERROR_MINI : ERROR_TIPS.ERROR;
      setErrorTip(error);
    } catch (error) {
      changeLoading(false);
      setErrorTip(ERROR_TIPS.ERROR);
    }
    return {};
  };

  const changeLoading = (bo: boolean) => {
    setLoading(bo);
    getColorStatus(bo);
  };

  const propsOnChange = (data: { primary: string; complementary: string }) => {
    setSample(data);
    onChange?.(data);
  };

  const handleGetColor = () => {
    getColors(url);
  };

  if (!isShowColorPicker) {
    return null;
  }

  const loadingStyle: CSSProperties = miniPicker
    ? {
      fontSize: 10,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }
    : {};

  return (
    <Loading
      showOverlay
      loading={loading}
      text={
        <span
          className={classNames(styles.loading, { [styles.mini]: miniPicker })}
        >
          {miniPicker ? '' : '取色中'}
        </span>
      }
      style={{ ...loadingStyle }}
      size={miniPicker ? 'small' : 'medium'}
    >
      {miniPicker ? (
        <>
          {errorTip ? (
            <Tooltip
              content={
                <div className={styles.miniTip}>
                  颜色获取失败，您可以点击 “取色按钮“
                  重新获取或者点击图片“编辑按钮“ 自己输入图片主色和补色
                </div>
              }
              destroyOnClose
              showArrow
              theme="default"
            >
              <Button
                theme="primary"
                disabled={disabledBtn}
                variant="outline"
                size="small"
                style={{
                  height: 28,
                  fontSize: 10,
                  width: 49,
                  padding: '2px 4px',
                }}
                onClick={handleGetColor}
              >
                点击取色
              </Button>
            </Tooltip>
          ) : (
            <Button
              theme="primary"
              disabled={disabledBtn}
              variant="outline"
              size="small"
              style={{
                height: 28,
                fontSize: 10,
                width: 49,
                padding: '2px 4px',
              }}
              onClick={handleGetColor}
            >
              点击取色
            </Button>
          )}
        </>
      ) : (
        <Space breakLine>
          <div>
            <ColorPicker
              defaultValue={defaultColor}
              value={sample.primary}
              format="HEX"
              colorModes={['monochrome']}
              onChange={value => handleOnChangeColor('primary', value)}
            />
          </div>
          <div>
            <ColorPicker
              defaultValue={defaultColor}
              value={sample.complementary}
              format="HEX"
              colorModes={['monochrome']}
              onChange={value => handleOnChangeColor('complementary', value)}
            />
          </div>
          <div className={styles.getColor}>
            <Button
              theme="primary"
              variant="outline"
              disabled={disabledBtn}
              size="small"
              style={{ height: 32, width: 88 }}
              onClick={handleGetColor}
            >
              重新取色
            </Button>
          </div>
        </Space>
      )}
      {errorTip && (
        <div
          className={classNames(
            styles.errorTip,
            'nova-text-error',
            { [styles.mini]: miniPicker },
          )}
        >
          {errorTip}
        </div>)}
    </Loading>
  );
};
